import { useEffect, useState } from 'react'
import { chaveDaDescricao } from '@domain/finance'
import type { CategoryRule, Category } from '@domain/finance'
import { normalizedText } from '@domain/product'
import { Botao } from '@ui/glass/Botao'
import { Glass } from '@ui/glass/Glass'
import { SeletorDeCategoria } from '@ui/finance/SeletorDeCategoria'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { Voltar } from '@ui/shell/Voltar'

/**
 * A tabela de referência, à vista — Fase 10, etapa 3.
 *
 * ## Por que ela precisa existir como tela
 *
 * O app passou a categorizar sozinho. Um aprendizado que ninguém consegue ver é
 * um aprendizado que ninguém consegue **desfazer**: a pessoa notaria a regra
 * errada pelo efeito dela, três meses de extrato depois, sem ter onde mexer. É
 * a mesma razão pela qual o `rawText` da nota fica visível — o princípio 5 pede
 * que a decisão da máquina seja inspecionável.
 *
 * ## Ordenada por quantas vezes aplicou
 *
 * É o peso da regra: a que categorizou 43 transações é a que importa conferir,
 * e a que aplicou uma vez pode esperar. Ordenar por nome poria "Amazon" na
 * frente de uma regra que decide metade do extrato.
 *
 * ## A chave aparece, em fonte mono, embaixo do rótulo
 *
 * Ela é o que de fato casa — `Dl*Uberrides 2/3`, `Uberrides` e `DL*UBERRIDES`
 * são a mesma `uberrides` —, e sem vê-la ninguém entende por que uma regra
 * pegou uma transação de nome diferente.
 *
 * ## "Nunca" fica no mesmo lugar que a categoria
 *
 * São a mesma decisão vista de dois jeitos: o que esta descrição recebe. Pôr o
 * "nunca" num interruptor à parte faria a tela ter dois controles que se
 * contradizem, e um estado onde os dois estão ligados.
 */
export function RegrasDeCategoria() {
  const { categoryRuleRepository, categoryRepository } = useFinanceRepositories()
  const [regras, setRegras] = useState<CategoryRule[] | 'loading' | 'failed'>('loading')
  const [categorias, setCategorias] = useState<Category[]>([])
  const [busca, setBusca] = useState('')
  const [comMenu, setComMenu] = useState<CategoryRule | null>(null)
  const [criando, setCriando] = useState(false)

  function recarregar() {
    void Promise.all([categoryRuleRepository.list(), categoryRepository.list()])
      .then(([lidas, cats]) => {
        setRegras(lidas)
        setCategorias(cats)
      })
      .catch((erro: unknown) => {
        console.error('Regras: a leitura falhou', erro)
        setRegras('failed')
      })
  }

  useEffect(() => {
    let vivo = true
    void Promise.all([categoryRuleRepository.list(), categoryRepository.list()])
      .then(([lidas, cats]) => {
        if (!vivo) return
        setRegras(lidas)
        setCategorias(cats)
      })
      .catch((erro: unknown) => {
        console.error('Regras: a leitura falhou', erro)
        if (vivo) setRegras('failed')
      })
    return () => {
      vivo = false
    }
  }, [categoryRuleRepository, categoryRepository])

  const termo = normalizedText(busca)
  const visiveis = Array.isArray(regras)
    ? regras.filter(
        (r) => termo === '' || normalizedText(r.label).includes(termo) || r.key.includes(termo),
      )
    : []

  /** Troca a decisão de uma regra e relê — a lista é ordenada pelo servidor. */
  function decidir(regra: CategoryRule, decision: CategoryRule['decision']) {
    setRegras((atual) =>
      Array.isArray(atual) ? atual.map((r) => (r.id === regra.id ? { ...r, decision } : r)) : atual,
    )
    void categoryRuleRepository
      .upsert({
        key: regra.key,
        label: regra.label,
        decision,
        origin: 'manual',
        scope: regra.scope,
      })
      .catch((erro: unknown) => {
        console.error('Regras: não deu para gravar', erro)
        recarregar()
      })
  }

  return (
    <section>
      <div className="mb-2">
        <Voltar para="/ajustes">Ajustes</Voltar>
      </div>
      <header className="mb-4 flex items-center justify-between gap-2">
        <h1 className="titulo min-w-0 truncate text-2xl">Regras de categoria</h1>
        <Botao
          type="button"
          tom={criando ? 'primario' : 'secundario'}
          aria-expanded={criando}
          onClick={() => {
            setCriando(!criando)
          }}
        >
          Nova regra
        </Botao>
      </header>

      <Glass level="frost" className="mb-4 p-5">
        <p className="mb-3 text-sm text-[var(--text-muted)]">
          O que o app aprendeu a categorizar sozinho. Toda transação nova com uma destas descrições
          nasce na categoria da regra, marcada como automática até você conferir.
        </p>

        <label className="mb-3 block">
          <span className="sr-only">Buscar regra</span>
          <input
            type="search"
            data-atalho-busca
            value={busca}
            placeholder="Buscar pela descrição"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
            onChange={(e) => {
              setBusca(e.target.value)
            }}
          />
        </label>

        {regras === 'loading' && (
          <p role="status" className="text-sm text-[var(--text-muted)]">
            Carregando…
          </p>
        )}
        {regras === 'failed' && (
          <p role="alert" className="text-sm text-[var(--text-muted)]">
            Não foi possível ler as regras agora.
          </p>
        )}
        {Array.isArray(regras) &&
          (visiveis.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">
              {regras.length === 0
                ? 'Nenhuma regra ainda. Ao categorizar uma transação, o app pergunta se aquilo vale sempre — e é daí que elas nascem.'
                : 'Nenhuma regra com esse nome.'}
            </p>
          ) : (
            <ul aria-label="Regras">
              {visiveis.map((regra) => (
                <li key={regra.id} className="border-b border-[var(--border)] py-2 text-sm">
                  <div className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate font-medium">{regra.label}</span>
                    {/* O peso da regra. Zero não aparece: a regra recém-criada
                        diria "0×" como se tivesse falhado em alguma coisa. */}
                    {regra.appliedCount > 0 && (
                      <span className="shrink-0 text-xs text-[var(--text-muted)] tabular-nums">
                        {`${String(regra.appliedCount)}×`}
                      </span>
                    )}
                  </div>
                  <p className="truncate font-mono text-xs text-[var(--text-muted)]">{regra.key}</p>

                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <SeletorDeCategoria
                      compacto
                      tom={regra.decision.kind === 'never' ? 'neutro' : 'automatico'}
                      categorias={categorias}
                      value={regra.decision.kind === 'category' ? regra.decision.categoryId : null}
                      rotulo={`Categoria da regra ${regra.label}: ${decisaoLegivel(regra, categorias)}`}
                      className="max-w-[60%]"
                      onCriou={recarregar}
                      onChange={(categoryId) => {
                        decidir(
                          regra,
                          categoryId === null
                            ? { kind: 'never' }
                            : { kind: 'category', categoryId },
                        )
                      }}
                    />
                    {regra.decision.kind === 'never' && (
                      <span className="text-xs text-[var(--text-muted)]">nunca sozinho</span>
                    )}
                    <button
                      type="button"
                      aria-label={`Mais ações da regra ${regra.label}`}
                      className="alvo ml-auto text-xs text-[var(--text-muted)] underline"
                      onClick={() => {
                        setComMenu(regra)
                      }}
                    >
                      opções
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ))}
      </Glass>

      {criando && (
        <NovaRegra
          categorias={categorias}
          onFechar={() => {
            setCriando(false)
          }}
          onCriar={(label, decision) => {
            const key = chaveDaDescricao(label)
            if (key === '') return
            void categoryRuleRepository
              .upsert({ key, label, decision, origin: 'manual', scope: 'transaction' })
              .then(recarregar)
              .catch((erro: unknown) => {
                console.error('Regras: não deu para criar', erro)
              })
            setCriando(false)
          }}
        />
      )}

      {comMenu !== null && (
        <FolhaDeBaixo
          titulo={`Ações da regra ${comMenu.label}`}
          onFechar={() => {
            setComMenu(null)
          }}
        >
          <p className="mb-2 truncate px-1 text-sm font-medium">{comMenu.label}</p>
          <div className="flex flex-col">
            {comMenu.decision.kind !== 'never' && (
              <button
                type="button"
                className="alvo justify-start rounded-lg px-3 text-left text-sm"
                onClick={() => {
                  decidir(comMenu, { kind: 'never' })
                  setComMenu(null)
                }}
              >
                Nunca categorizar sozinho
              </button>
            )}
            {/* Apagar é diferente de "nunca": a regra some, e a pergunta "valer
                sempre?" volta na próxima vez que alguém categorizar isto. */}
            <button
              type="button"
              className="alvo justify-start rounded-lg border-t border-[var(--border)] px-3 text-left text-sm text-[var(--negative)]"
              onClick={() => {
                const alvo = comMenu
                setComMenu(null)
                setRegras((atual) =>
                  Array.isArray(atual) ? atual.filter((r) => r.id !== alvo.id) : atual,
                )
                void categoryRuleRepository.remove(alvo.id).catch((erro: unknown) => {
                  console.error('Regras: não deu para apagar', erro)
                  recarregar()
                })
              }}
            >
              Apagar a regra
            </button>
            <button
              type="button"
              className="alvo justify-start rounded-lg px-3 text-left text-sm text-[var(--text-muted)]"
              onClick={() => {
                setComMenu(null)
              }}
            >
              Fechar
            </button>
          </div>
        </FolhaDeBaixo>
      )}
    </section>
  )
}

/** O que a regra faz, em palavras — para o rótulo do seletor. */
function decisaoLegivel(regra: CategoryRule, categorias: Category[]): string {
  const decision = regra.decision
  if (decision.kind === 'never') return 'nunca'
  return categorias.find((c) => c.id === decision.categoryId)?.name ?? 'categoria apagada'
}

/**
 * Criar uma regra à mão, sem esperar uma transação aparecer.
 *
 * A chave sai da **mesma** normalização de sempre: quem digita "Uber" cria a
 * regra de `uber`, e é isso que ela vai casar. Mostrar a chave enquanto se
 * digita é o que evita a regra que "não funciona" porque a pessoa colou o
 * extrato inteiro no campo.
 */
function NovaRegra({
  categorias,
  onCriar,
  onFechar,
}: {
  categorias: Category[]
  onCriar: (label: string, decision: CategoryRule['decision']) => void
  onFechar: () => void
}) {
  const [texto, setTexto] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const chave = chaveDaDescricao(texto)

  return (
    <FolhaDeBaixo titulo="Nova regra" onFechar={onFechar}>
      <h2 className="titulo mb-3 text-base">Nova regra</h2>

      <label className="mb-1 block text-xs text-[var(--text-muted)]" htmlFor="regra-descricao">
        Descrição como aparece no extrato
      </label>
      <input
        id="regra-descricao"
        value={texto}
        placeholder="Dl*Uberrides"
        className="mb-1 w-full rounded-lg border border-[var(--border)] bg-transparent px-3 py-2 text-sm"
        onChange={(e) => {
          setTexto(e.target.value)
        }}
      />
      <p className="mb-3 font-mono text-xs text-[var(--text-muted)]">
        {chave === '' ? 'Sem chave: só números não viram regra.' : `Vai casar: ${chave}`}
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--text-muted)]">Categoria</span>
        <SeletorDeCategoria
          categorias={categorias}
          value={categoryId}
          rotulo="Categoria da regra nova"
          className="min-w-40 flex-1"
          onCriou={() => undefined}
          onChange={setCategoryId}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Botao
          type="button"
          tom="primario"
          disabled={chave === '' || categoryId === null}
          onClick={() => {
            if (categoryId !== null) onCriar(texto.trim(), { kind: 'category', categoryId })
          }}
        >
          Criar
        </Botao>
        <Botao
          type="button"
          disabled={chave === ''}
          onClick={() => {
            onCriar(texto.trim(), { kind: 'never' })
          }}
        >
          Nunca categorizar
        </Botao>
        <button
          type="button"
          onClick={onFechar}
          className="alvo px-3 text-sm text-[var(--text-muted)]"
        >
          Cancelar
        </button>
      </div>
    </FolhaDeBaixo>
  )
}
